import './styles/ArticleItem.scss';

import { Grid, Paper } from '@mui/material';

import clsx from 'clsx';
import { addMinutes, format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { translations } from 'locales/i18n';
import { Article } from 'types/AppTypes';

import ArticleTypeLogo from './components/ArticleTypeLogo';

interface Props {
  article: Article;
  dense?: boolean;
  noOpen?: boolean;
  back?: string;
}

export const ArticleItem: FunctionComponent<Props> = ({ article, dense, noOpen, back }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const articleType = () => {
    switch (article.type) {
      case 'general':
        return t(translations.pages.articles.type.general());
      case 'study':
        return t(translations.pages.articles.type.study());
      case 'event':
        return t(translations.pages.articles.type.event());
      case 'legal':
        return t(translations.pages.articles.type.legal());
      case 'holiday':
        return t(translations.pages.articles.type.holiday());
    }
  };

  const appointment = () => {
    if (
      article.withAppointment !== undefined &&
      article.withAppointment === false &&
      article.type !== 'event' &&
      article.type !== 'legal' &&
      article.type !== 'holiday'
    ) {
      return;
    }
    if (article.appointment.allDay) {
      if (
        article.appointment.endDate &&
        new Date(article.appointment.startDate).toLocaleDateString() !==
          new Date(article.appointment.endDate).toLocaleDateString()
      ) {
        return (
          <Grid container className="article__info__appointment">
            {t(translations.pages.articles.appointmentFormat(), {
              startTime: t(translations.pages.articles.allDay()),
              startDate: new Date(article.appointment.startDate).toLocaleDateString(),
              seperator2: ' - ',
              endDate: new Date(article.appointment.endDate).toLocaleDateString()
            })}
          </Grid>
        );
      } else {
        return (
          <Grid container className="article__info__appointment">
            {t(translations.pages.articles.appointmentFormat(), {
              startTime: t(translations.pages.articles.allDay()),
              startDate: new Date(article.appointment.startDate).toLocaleDateString()
            })}
          </Grid>
        );
      }
    } else {
      if (article.appointment.duration) {
        return (
          <Grid container className="article__info__appointment">
            {t(translations.pages.articles.appointmentFormat(), {
              startTime: format(new Date(article.appointment.startDate), 'hh:mm aa', {
                locale: enUS
              }),
              seperator1: ' - ',
              endTime: format(
                addMinutes(new Date(article.appointment.startDate), article.appointment.duration),
                'hh:mm aa',
                { locale: enUS }
              ),
              startDate: format(new Date(article.appointment.startDate), 'EEEE, MMMM do')
            })}
          </Grid>
        );
      }
      return (
        <Grid container className="article__info__appointment">
          {t(translations.pages.articles.appointmentFormat(), {
            startTime: format(new Date(article.appointment.startDate), 'hh:mm aa', {
              locale: enUS
            }),
            seperator1: ' - ',
            endTime: format(new Date(article.appointment.endDate), 'hh:mm aa', { locale: enUS }),
            startDate: format(new Date(article.appointment.startDate), 'dd.MM.yyyy')
          })}
        </Grid>
      );
    }
  };

  const ArticleTypeDisplay = () => (
    <>
      <Grid container className="article__info__type">
        <Grid
          container
          className={clsx({
            'article__info__type--general': article.type === 'general',
            'article__info__type--study': article.type === 'study',
            'article__info__type--event': article.type === 'event',
            'article__info__type--legal': article.type === 'legal',
            'article__info__type--holiday': article.type === 'holiday'
          })}
        >
          {articleType()}
        </Grid>
      </Grid>
      {!dense && (
        <Grid container className="article__info__start-date">
          {article.department && article.department.title + ' / '}
          {`${article.author.firstName} ${article.author.lastName}`} /{' '}
          {new Date(article.startDate).toLocaleDateString()}
          {article.appointment && appointment()}
        </Grid>
      )}
    </>
  );

  return (
    <>
      <Grid
        container
        className={clsx('article', {
          'article--dense': dense,
          'article--openable': !noOpen
        })}
        component={Paper}
        square
        onClick={() =>
          !noOpen && navigate(`/articles/${article.id}`, { state: { back: back ?? '' } })
        }
      >
        <Grid item xs={dense ? 3 : 4}>
          <Grid container className="info centered">
            <Grid item md={dense ? 12 : 2} className={clsx('logo', { 'logo--dense': dense })}>
              <ArticleTypeLogo articleType={article.type} />
            </Grid>
            {!dense && (
              <Grid item md={8}>
                {ArticleTypeDisplay()}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={dense ? 9 : 8}>
          {dense && <Grid container>{ArticleTypeDisplay()}</Grid>}
          <Grid container className="article__info__headline">
            {article.headline}
          </Grid>
          {!dense && (
            <>
              <Grid container className="article__info__text">
                {article.shortDescription}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};
